import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { USER_EMAIL_ASCII_PATTERN, USER_EMAIL_NON_ASCII_MESSAGE } from '@efacture/core';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowRight,
  BookOpen,
  Calculator,
  FolderKanban,
  Package,
  Receipt,
  Shield,
  ShoppingCart,
  Store,
  Wallet,
  type LucideIcon,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input, PasswordInput } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useTranslation, type TranslationKey } from '@/lib/i18n';
import { useAuth, TwoFARequiredError } from '@/contexts/AuthContext';
import { usePasskey } from '@/hooks/usePasskey';
import { apiClient, ApiError } from '@/api/client';
import { useResendVerification } from '@/api/queries/onboarding';
import { isDesktopApp } from '@/lib/desktop';
import brandLogo from '@/assets/brand-logo.png';

// ─────────────────────────────────────────────────────────────
// Validation Schema
// ─────────────────────────────────────────────────────────────

const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'Ce champ est obligatoire')
    .regex(USER_EMAIL_ASCII_PATTERN, USER_EMAIL_NON_ASCII_MESSAGE)
    .email('Adresse email invalide'),
  password: z
    .string()
    .min(1, 'Le mot de passe est obligatoire')
    .min(8, 'Le mot de passe doit contenir au moins 8 caractères'),
  rememberMe: z.boolean(),
});

type LoginFormData = z.infer<typeof loginSchema>;

// ─────────────────────────────────────────────────────────────
// Decorative Patterns
// ─────────────────────────────────────────────────────────────

function GeometricPattern() {
  return (
    <svg
      className="absolute inset-0 w-full h-full opacity-[0.07]"
      viewBox="0 0 400 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <pattern id="africanPattern" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
          {/* Diamond shapes inspired by West African textiles */}
          <path d="M40 0 L80 40 L40 80 L0 40 Z" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M40 15 L65 40 L40 65 L15 40 Z" fill="none" stroke="currentColor" strokeWidth="1" />
          <circle cx="40" cy="40" r="5" fill="currentColor" />
          <circle cx="0" cy="0" r="3" fill="currentColor" />
          <circle cx="80" cy="0" r="3" fill="currentColor" />
          <circle cx="0" cy="80" r="3" fill="currentColor" />
          <circle cx="80" cy="80" r="3" fill="currentColor" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#africanPattern)" />
    </svg>
  );
}

interface ModuleTile {
  icon: LucideIcon;
  label: TranslationKey;
}

const MODULE_TILES: ModuleTile[] = [
  { icon: Receipt, label: 'login.modules.invoicing' },
  { icon: ShoppingCart, label: 'login.modules.pos' },
  { icon: Package, label: 'login.modules.stock' },
  { icon: BookOpen, label: 'login.modules.accounting' },
  { icon: Wallet, label: 'login.modules.payroll' },
  { icon: FolderKanban, label: 'login.modules.projects' },
];

function ModuleMosaic({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <ul aria-label={t('login.modules.label')} className={className}>
      {MODULE_TILES.map((tile) => (
        <li
          key={tile.label}
          className="flex w-[4.75rem] flex-col items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-1 py-3 shadow-2xl backdrop-blur-sm 2xl:w-[5.5rem]"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-turquoise/20">
            <tile.icon className="h-5 w-5 text-brand-turquoise" aria-hidden="true" />
          </span>
          <span className="text-[11px] font-medium text-white/90 2xl:text-xs">{t(tile.label)}</span>
        </li>
      ))}
    </ul>
  );
}

// ─────────────────────────────────────────────────────────────
// Features List
// ─────────────────────────────────────────────────────────────

interface FeatureCopy {
  title: TranslationKey;
  description: TranslationKey;
}

interface Feature extends FeatureCopy {
  icon: LucideIcon;
}

const features: Feature[] = [
  {
    icon: Store,
    title: 'login.feature.commerce.title',
    description: 'login.feature.commerce.description',
  },
  {
    icon: Calculator,
    title: 'login.feature.accounting.title',
    description: 'login.feature.accounting.description',
  },
  {
    icon: Shield,
    title: 'login.feature.security.title',
    description: 'login.feature.security.description',
  },
];

// ─────────────────────────────────────────────────────────────
// Login Page Component
// ─────────────────────────────────────────────────────────────

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // Disambiguation when the same email opens more than one dossier
  // (User.email is unique PER TENANT, @@unique([tenantId, email]), not
  // globally). The login form itself has no tenant field — bookmarkable
  // `/login?tenant=<slug>` is the escape hatch, both for a real admin who
  // manages several dossiers with one address and for E2E, whose seeded
  // roles share one password across several tenants by design.
  const [searchParams] = useSearchParams();
  const tenantSlug = searchParams.get('tenant') || undefined;
  const { login, loginWithTokens, isLoading: authLoading } = useAuth();
  const { isSupported: passkeySupported, isLoading: passkeyLoading, loginWithPasskey } = usePasskey();
  // The desktop shell offers no passkey sign-in; the browser build keeps it.
  const showPasskeyLogin = passkeySupported && !isDesktopApp();
  const [isLoading, setIsLoading] = useState(false);
  const [twoFAState, setTwoFAState] = useState<{ token: string; code: string } | null>(null);
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFAError, setTwoFAError] = useState('');
  // Activation par e-mail : e-mail non vérifié → invite à vérifier/renvoyer.
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  // Dossiers candidats renvoyés par un refus TENANT_AMBIGU : le mot de passe
  // saisi ouvre PLUSIEURS dossiers pour cette adresse. Sans cette liste,
  // l'erreur demande « précisez le dossier » alors que le formulaire n'offre
  // aucun moyen de le faire — impasse. On les affiche pour que l'utilisateur
  // choisisse, et on rejoue la connexion avec le slug retenu.
  const [ambiguousTenants, setAmbiguousTenants] = useState<
    { slug: string; name: string }[] | null
  >(null);
  const resendVerification = useResendVerification();

  // A deep link the guard interrupted wins; otherwise the root, which
  // resolves the landing route once the sector profile is known
  // (LandingRedirect). Resolving here is impossible: the profile is only
  // fetched after authentication.
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });

  const rememberMe = watch('rememberMe');

  /**
   * Extrait la liste des dossiers candidats d'un refus TENANT_AMBIGU.
   * `details` est volontairement typé `unknown` côté client (voir ApiError) :
   * on le rétrécit ICI, au point de consommation, plutôt que de faire
   * confiance à une forme non vérifiée.
   */
  const readAmbiguousTenants = (
    details: Record<string, unknown> | undefined
  ): { slug: string; name: string }[] | null => {
    const tenants = details?.['tenants'];
    if (!Array.isArray(tenants)) return null;
    const parsed = tenants.filter(
      (entry): entry is { slug: string; name: string } =>
        typeof entry === 'object' &&
        entry !== null &&
        typeof (entry as { slug?: unknown }).slug === 'string' &&
        typeof (entry as { name?: unknown }).name === 'string'
    );
    return parsed.length > 0 ? parsed : null;
  };

  const onSubmit = async (data: LoginFormData, overrideTenantSlug?: string) => {
    setIsLoading(true);
    // Une nouvelle tentative repart d'un état propre : sinon le panneau de
    // choix des dossiers resterait affiché après un identifiant corrigé.
    setAmbiguousTenants(null);
    try {
      await login({
        email: data.email,
        password: data.password,
        rememberMe: data.rememberMe,
        tenantSlug: overrideTenantSlug ?? tenantSlug,
      });

      toast.success(t('auth.loginSuccess'));
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof TwoFARequiredError) {
        setTwoFAState({ token: err.twoFAToken, code: '' });
        return;
      }
      // Compte non vérifié (inscription self-serve) : afficher l'invite à
      // vérifier l'adresse plutôt qu'une erreur d'identifiants générique.
      if (err instanceof ApiError && err.code === 'EMAIL_NON_VERIFIE') {
        setUnverifiedEmail(data.email);
        return;
      }
      // Adresse partagée par plusieurs dossiers (même email, tenants
      // distincts — @@unique([tenantId, email])) : le mot de passe saisi
      // ouvre plus d'un dossier, la connexion refuse plutôt que d'en
      // choisir un au hasard. Le message backend nomme déjà la situation ;
      // le noyer sous "identifiants invalides" serait trompeur — le mot de
      // passe était CORRECT.
      if (err instanceof ApiError && err.code === 'TENANT_AMBIGU') {
        const tenants = readAmbiguousTenants(err.details);
        if (tenants) {
          // On propose le choix : sans lui, le message « précisez le
          // dossier » serait une impasse (aucun champ dossier au formulaire).
          setAmbiguousTenants(tenants);
        } else {
          // L'API a refusé sans nommer les dossiers (forme inattendue) :
          // on affiche au moins son message plutôt que « identifiants
          // invalides », qui serait faux — le mot de passe était BON.
          toast.error(err.message);
        }
        return;
      }
      toast.error(t('auth.invalidCredentials'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = () => {
    if (!unverifiedEmail) return;
    resendVerification.mutate(unverifiedEmail, {
      onSuccess: () =>
        toast.success(
          'Si un compte existe pour cette adresse, un nouvel e-mail de vérification a été envoyé.'
        ),
      onError: () =>
        toast.error("Échec de l'envoi. Réessayez dans quelques instants."),
    });
  };

  const onTwoFASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setTwoFAError('');
    try {
      const response = await apiClient.post<import('@/types').AuthResponse>('/auth/2fa/verify', {
        twoFAToken: twoFAState!.token,
        code: twoFACode,
      });
      loginWithTokens(response as any);
      toast.success(t('auth.loginSuccess'));
      navigate(from, { replace: true });
    } catch (err) {
      // A lockout is not a wrong code: the API's message names the wait.
      setTwoFAError(
        err instanceof ApiError && err.status === 429 && err.message
          ? err.message
          : 'Code invalide. Veuillez réessayer.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasskeyLogin = async () => {
    try {
      const emailValue = watch("email");
      const result = await loginWithPasskey(emailValue || undefined);
      loginWithTokens(result as any);
      navigate(from, { replace: true });
    } catch {
      // Error already shown by usePasskey hook via toast
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* ─────────────────────────────────────────────────────── */}
      {/* Left Side - Branding */}
      {/* ─────────────────────────────────────────────────────── */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] relative overflow-hidden bg-gradient-to-br from-brand-lagune-800 via-brand-lagune-900 to-brand-lagune-950">
        {/* Geometric Pattern Overlay */}
        <GeometricPattern />

        {/* Gold accent gradient */}
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-bl from-brand-turquoise/10 to-transparent" />
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-brand-turquoise/5 to-transparent" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 text-white w-full">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <img src={brandLogo} alt={t('brand.name')} className="w-12 h-12 rounded-xl shadow-lg" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight">{t('brand.name')}</h1>
              <p className="text-sm text-brand-lagune-200/80">{t('brand.baseline')}</p>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 flex items-center gap-6 2xl:gap-10">
            <div className="max-w-lg">
              <h2 className="text-4xl 2xl:text-5xl font-bold leading-tight mb-6">
                {t('login.hero.lead')} <span className="text-brand-turquoise">{t('login.hero.accent')}</span>{' '}
                <br />
                {t('login.hero.tail')}
              </h2>
              <p className="text-lg text-brand-lagune-100/80 mb-10 leading-relaxed">
                {t('login.hero.body')}
              </p>

              {/* Features */}
              <div className="space-y-5">
                {features
                  .map((feature, index) => (
                    <div
                      key={feature.title}
                      className="flex items-start gap-4 opacity-0 animate-fade-in-up"
                      style={{ animationDelay: `${index * 150}ms`, animationFillMode: 'forwards' }}
                    >
                      <div className="w-10 h-10 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                        <feature.icon className="w-5 h-5 text-brand-turquoise" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-white">{t(feature.title)}</h3>
                        <p className="text-sm text-brand-lagune-100/80">{t(feature.description)}</p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* Suite modules — in the flow, so it never overlaps the text column */}
            <ModuleMosaic className="hidden xl:grid grid-cols-2 gap-2 flex-shrink-0 ml-auto opacity-80 2xl:gap-3" />
          </div>

          {/* Footer */}
          <div className="text-sm text-brand-lagune-100/70">
            © 2026 {t('brand.name')}. Tous droits réservés.
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────── */}
      {/* Right Side - Login Form */}
      {/* ─────────────────────────────────────────────────────── */}
      <div className="w-full lg:w-1/2 xl:w-[45%] flex items-center justify-center p-6 sm:p-12 bg-gradient-to-b from-slate-50 to-white">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <img src={brandLogo} alt={t('brand.name')} className="w-11 h-11 rounded-xl shadow-lg" />
            <div>
              <h1 className="text-xl font-bold text-slate-900">{t('brand.name')}</h1>
              <p className="text-xs text-slate-500">{t('brand.baseline')}</p>
            </div>
          </div>

          {/* 2FA Vérification Form */}
          {twoFAState ? (
            <>
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-slate-900 mb-2">
                  Vérification 2FA
                </h2>
                <p className="text-slate-500">
                  Entrez le code à 6 chiffres de votre application d'authentification, ou un code de récupération.
                </p>
              </div>
              <form onSubmit={onTwoFASubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="totp-code" required>Code de vérification</Label>
                  <Input
                    id="totp-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="000000"
                    maxLength={9}
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value)}
                    error={!!twoFAError}
                    autoFocus
                  />
                  {twoFAError && (
                    <p className="text-sm text-destructive">{twoFAError}</p>
                  )}
                </div>
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-brand-lagune-700 to-brand-lagune-800 hover:from-brand-lagune-800 hover:to-brand-lagune-900 h-12 text-base"
                  loading={isLoading}
                  disabled={isLoading || twoFACode.length < 6}
                >
                  Vérifier
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <button
                  type="button"
                  onClick={() => { setTwoFAState(null); setTwoFACode(''); setTwoFAError(''); }}
                  className="w-full text-sm text-slate-500 hover:text-slate-700 transition-colors"
                >
                  Retour à la connexion
                </button>
              </form>
            </>
          ) : (
          <>
          {/* Form Header */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">
              {t('auth.welcome')}
            </h2>
            <p className="text-slate-500">
              {t('auth.subtitle')}
            </p>
          </div>

          {/* Activation par e-mail : compte non vérifié */}
          {unverifiedEmail && (
            <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 p-4">
              <p className="text-sm font-semibold text-amber-900">
                Vérifiez votre adresse e-mail
              </p>
              <p className="mt-1 text-sm text-amber-800">
                Votre compte n'est pas encore activé. Ouvrez l'e-mail de
                vérification envoyé à <strong>{unverifiedEmail}</strong> et
                cliquez sur le lien, puis reconnectez-vous.
              </p>
              <Button
                type="button"
                variant="outline"
                className="mt-3"
                onClick={handleResend}
                disabled={resendVerification.isPending}
              >
                {resendVerification.isPending
                  ? 'Envoi…'
                  : "Renvoyer l'e-mail de vérification"}
              </Button>
            </div>
          )}

          {/* Adresse partagée par plusieurs dossiers : laisser choisir.
              Sans ce bloc, le refus TENANT_AMBIGU demanderait de « préciser
              le dossier » sans qu'aucun champ ne le permette. */}
          {ambiguousTenants && (
            <div className="mb-6 rounded-xl border border-sky-300 bg-sky-50 p-4">
              <p className="text-sm font-semibold text-sky-900">
                Plusieurs dossiers pour cette adresse
              </p>
              <p className="mt-1 text-sm text-sky-800">
                Vos identifiants ouvrent plusieurs dossiers. Choisissez celui
                auquel vous souhaitez vous connecter.
              </p>
              <div className="mt-3 flex flex-col gap-2">
                {ambiguousTenants.map((tenant) => (
                  <Button
                    key={tenant.slug}
                    type="button"
                    variant="outline"
                    className="justify-start"
                    disabled={isLoading}
                    onClick={() =>
                      handleSubmit((data) => onSubmit(data, tenant.slug))()
                    }
                  >
                    {tenant.name}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Login Form.
              `(data) => onSubmit(data)` et NON `onSubmit` directement :
              react-hook-form appelle le handler avec (data, event), et
              l'event atterrirait dans le 2e paramètre `overrideTenantSlug`,
              qui serait alors envoyé comme slug de dossier. */}
          <form
            onSubmit={handleSubmit((data) => onSubmit(data))}
            noValidate
            className="space-y-5"
          >
            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" error={!!errors.email} required>
                {t('auth.email')}
              </Label>
              <Input
                id="email"
                type="email"
                placeholder={t('auth.email.placeholder')}
                autoComplete="email"
                error={!!errors.email}
                {...register('email')}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" error={!!errors.password} required>
                  {t('auth.password')}
                </Label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-brand-lagune-700 hover:text-brand-lagune-800 font-medium transition-colors"
                >
                  {t('auth.forgotPassword')}
                </Link>
              </div>
              <PasswordInput
                id="password"
                placeholder={t('auth.password.placeholder')}
                autoComplete="current-password"
                error={!!errors.password}
                {...register('password')}
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </div>

            {/* Remember Me */}
            <div className="flex items-center gap-3">
              <Checkbox
                id="rememberMe"
                checked={rememberMe}
                onCheckedChange={(checked) => setValue('rememberMe', checked === true)}
              />
              <Label
                htmlFor="rememberMe"
                className="text-sm font-normal text-slate-600 cursor-pointer"
              >
                {t('auth.rememberMe')}
              </Label>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-brand-lagune-700 to-brand-lagune-800 hover:from-brand-lagune-800 hover:to-brand-lagune-900 h-12 text-base"
              loading={isLoading || authLoading}
              disabled={isLoading || authLoading}
            >
              {t('auth.login')}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </form>

          {/* Passkey Login */}
          {showPasskeyLogin && (
            <>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white px-4 text-slate-500">OU</span>
                </div>
              </div>

              <button
                type="button"
                onClick={handlePasskeyLogin}
                disabled={passkeyLoading}
                className="w-full flex items-center justify-center gap-3 rounded-xl border-2 border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 hover:border-brand-lagune-300 transition-all duration-200 disabled:opacity-50"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 18v3c0 .6.4 1 1 1h4v-3h3v-3h2l1.4-1.4a6.5 6.5 0 1 0-4-4Z" />
                  <circle cx="16.5" cy="7.5" r=".5" fill="currentColor" />
                </svg>
                {passkeyLoading ? "Connexion en cours..." : "Se connecter avec un Passkey"}
              </button>
            </>
          )}

          {/* Entry point to the signup funnel. /inscription is a live route
              (router.tsx) that nothing linked to: it was reachable only by
              typing the URL. */}
          <p className="mt-8 text-center text-sm text-slate-600">
            Pas encore de compte ?{' '}
            <Link
              to="/inscription"
              className="font-medium text-brand-lagune-700 transition-colors hover:text-brand-lagune-800"
            >
              Créer un compte
            </Link>
          </p>

          {/* Trust Badges */}
          <div className="mt-10 pt-8 border-t border-slate-200">
            <div className="flex items-center justify-center gap-6 text-slate-500">
              <div className="flex items-center gap-2 text-xs">
                <Shield className="w-4 h-4" />
                <span>SSL Sécurisé</span>
              </div>
            </div>
          </div>
          </>
          )}
        </div>
      </div>

      {/* Custom Animations */}
      <style>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out;
        }
      `}</style>
    </div>
  );
}

export default LoginPage;
